import { Static, Type } from "@sinclair/typebox";
import { ApiErrorResponse } from "../ApiErrorResponse";
import { ConfigKey, OrgConfigs } from "@whatslly/types/OrgConfigs";

const flattenResources = (resources: Record<string, any>, prefix = ""): Record<string, string> => {
  return Object.keys(resources).reduce<Record<string, string>>((acc, key) => {
    if (typeof resources[key] === "string") {
      acc[`${prefix}${key}`] = resources[key];
    } else {
      acc = { ...acc, ...flattenResources(resources[key], `${prefix}${key}.`) };
    }
    return acc;
  }, {});
};

export const SIDECAR_STATUS = {
  BOOTING: "booting",
  RUNNING: "running",
  IDLE: "idle",
  PAIRING: "pairing",
  ERROR: "error",
  INVITED: "invited",
  NOT_PAIRED: "not_paired",
} as const;

export const SIDECAR_PAIRING_ERROR = {
  OPERATION_ABORTED: "The operation was aborted",
  FAILED_TO_PAIR: "Pairing failed",
  PHONE_MISMATCH: "Phone number mismatch",
};

export const SIDECAR_RUNNERS = ["k8s", "spwan"] as const;

const SidecarStatusSchema = Type.Union([
  Type.Object({ status: Type.Literal(SIDECAR_STATUS.BOOTING) }),
  Type.Object({ status: Type.Literal(SIDECAR_STATUS.RUNNING) }),
  Type.Object({
    status: Type.Literal(SIDECAR_STATUS.IDLE),
    state: Type.Optional(Type.Object({ byUser: Type.Boolean(), error: Type.Optional(Type.String()) })),
  }),
  Type.Object({
    status: Type.Literal(SIDECAR_STATUS.PAIRING),
    state: Type.Object({
      mode: Type.Union([Type.Literal("QR"), Type.Literal("phoneNumber"), Type.Literal("QR_AUTH_CODE")]),
      code: Type.String(),
      phone: Type.Optional(Type.String()),
    }),
  }),
  Type.Object({ status: Type.Literal(SIDECAR_STATUS.ERROR), state: Type.Object({ error: Type.String() }) }),
]);

export const SidecarMetadata = Type.Object({
  phone: Type.String(),
  isBusiness: Type.Optional(Type.Boolean()),
  inviterEmail: Type.Optional(Type.String()),
  inviterUserName: Type.Optional(Type.String()),
  formatted: Type.Optional(Type.String()),
  isPaired: Type.Optional(Type.Boolean()),
  pairedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
  pairedDevices: Type.Optional(Type.Number()),
  pairingSuccesses: Type.Optional(Type.Number()),
  pairingAttempts: Type.Optional(Type.Number()),
  autoReinvite: Type.Optional(Type.Boolean()),
  periodicReinvite: Type.Optional(Type.Boolean()),
  notifyEmail: Type.Optional(Type.Boolean()),
  notifyInviterOnSidecarEvents: Type.Optional(Type.Boolean()),
  locale: Type.Optional(Type.String()),
  imSessionId: Type.Optional(Type.String()),
  isDisconnectedByUser: Type.Optional(Type.Boolean()),
  disconnectedError: Type.Optional(Type.String()),
  disconnectedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
  proxyCountry: Type.Optional(Type.Union([Type.Literal("us"), Type.Literal("br")])),
});

const SidecarRunMode = Type.Union([
  Type.Object({ mode: Type.Literal("permanent"), verbose: Type.Optional(Type.Boolean()) }),
  Type.Object({ mode: Type.Literal("cron"), verbose: Type.Optional(Type.Boolean()), cron: Type.String() }),
]);

const SidecarBase = Type.Object({
  _id: Type.String(),
  orgId: Type.String(),
  uid: Type.String(),
  webhookSecret: Type.String(),
  pairingToken: Type.String(),
  userdataFile: Type.Optional(Type.String()),
  destinations: Type.Optional(Type.Array(Type.String())),
  createdAt: Type.String({ format: "date-time" }),
  updatedAt: Type.String({ format: "date-time" }),
  invitedToPairAt: Type.Number(),
  metadata: Type.Optional(SidecarMetadata),
  runMode: SidecarRunMode,
  runnerState: Type.Union([Type.Object({ type: Type.Literal("k8s") }), Type.Object({ type: Type.Literal("spwan") })]),
  runnerChannel: Type.Union([
    Type.Object({ type: Type.Literal("whatsapp") }),
    Type.Object({ type: Type.Literal("telegram") }),
    Type.Object({ type: Type.Literal("line") }),
    Type.Object({ type: Type.Literal("signal") }),
  ]),
  displayStatus: Type.Union([
    Type.Literal(SIDECAR_STATUS.IDLE),
    Type.Literal(SIDECAR_STATUS.RUNNING),
    Type.Literal(SIDECAR_STATUS.BOOTING),
    Type.Literal(SIDECAR_STATUS.ERROR),
    Type.Literal(SIDECAR_STATUS.INVITED),
    Type.Literal(SIDECAR_STATUS.NOT_PAIRED),
  ]),
});

export const SidecarSchema = Type.Intersect([SidecarBase, SidecarStatusSchema]);
export type Sidecar = Static<typeof SidecarSchema>;

export const ClientSideSidecar = Type.Intersect([
  Type.Pick(SidecarBase, [
    "createdAt",
    "updatedAt",
    "_id",
    "metadata",
    "destinations",
    "runMode",
    "orgId",
    "uid",
    "displayStatus",
    "runnerState",
    "runnerChannel",
  ]),
  SidecarStatusSchema,
  Type.Object({
    user: Type.Optional(
      Type.Object({
        userId: Type.String(),
        name: Type.String(),
        username: Type.Optional(Type.String()),
        email: Type.Optional(Type.String()),
        groupIds: Type.Optional(Type.Array(Type.String())),
        manager: Type.Optional(
          Type.Object({
            managerId: Type.String(),
            name: Type.String(),
          })
        ),
      })
    ),
  }),
]);
export type ClientSideSidecar = Static<typeof ClientSideSidecar>;

export const SidecarSettingsSchema = Type.Object({
  runMode: SidecarRunMode,
  notifyEmail: Type.Boolean(),
  autoReinvite: Type.Boolean(),
  periodicReinvite: Type.Boolean(),
  locale: Type.String(),
  notifyInviterOnSidecarEvents: Type.Optional(Type.Boolean()),
  proxyCountry: Type.Optional(Type.Union([Type.Literal("us"), Type.Literal("br")])),
});
export type SidecarSettings = Static<typeof SidecarSettingsSchema>;

export const UpdateSidecarSettingsBulk = Type.Object({
  ids: Type.Array(Type.String()),
  settings: Type.Object({
    notifyEmail: Type.Optional(Type.Boolean()),
    autoReinvite: Type.Optional(Type.Boolean()),
    periodicReinvite: Type.Optional(Type.Boolean()),
  }),
});
export type UpdateSidecarSettingsBulk = Static<typeof UpdateSidecarSettingsBulk>;

export const UpdateSidecarSchema = Type.Union([Type.Object({ success: Type.Literal(true) }), ApiErrorResponse]);
export type UpdateSidecar = Static<typeof UpdateSidecarSchema>;

export const ReInviteSidecar = Type.Union([Type.Object({ success: Type.Literal(true), pairingUrl: Type.String() }), ApiErrorResponse]);
export type ReInviteSidecar = Static<typeof ReInviteSidecar>;

export const SidecarChannelType = Type.Union([Type.Literal("whatsapp"), Type.Literal("telegram"), Type.Literal("line"), Type.Literal("signal")]);
export type SidecarChannelType = Static<typeof SidecarChannelType>;
export const CreateSidecarSchema = Type.Object({
  settings: SidecarSettingsSchema,
  inviteUrl: Type.Optional(Type.String()),
  runnerType: Type.Optional(Type.Union([Type.Literal("k8s"), Type.Literal("spwan")])),
  channelType: Type.Optional(SidecarChannelType),
  invites: Type.Array(
    Type.Object({
      expectedPhone: Type.Optional(Type.String()),
      uid: Type.String(),
      notifyInviterOnSidecarEvents: Type.Optional(Type.Boolean()),
    })
  ),
});
export type CreateSidecar = Static<typeof CreateSidecarSchema>;
export const CreateSidecarResponse = Type.Union([
  Type.Object({
    success: Type.Literal(true),
    pairingUrl: Type.Array(Type.String()),
  }),
  ApiErrorResponse,
]);
export type CreateSidecarResponse = Static<typeof CreateSidecarResponse>;

export const GetSidecarStatusSchema = Type.Union([Type.Object({ success: Type.Literal(true), sidecar: ClientSideSidecar }), ApiErrorResponse]);
export type GetSidecarStatus = Static<typeof GetSidecarStatusSchema>;

export const GetSidecarPairingConfigSchema = Type.Union([
  Type.Object({
    success: Type.Literal(true),
    customErrorText: Type.Optional(Type.String()),
  }),
  ApiErrorResponse,
]);
export type GetSidecarPairingConfig = Static<typeof GetSidecarPairingConfigSchema>;

export const MaxDevicesPerChannelSchema = Type.Object({
  max_devices_whatsapp: Type.Number(),
  max_devices_telegram: Type.Number(),
  max_devices_line: Type.Number(),
  max_devices_signal: Type.Number(),
});
export type MaxDevicesPerChannel = Static<typeof MaxDevicesPerChannelSchema>;

export const ListSidecarsSchema = Type.Union([
  Type.Object({
    success: Type.Literal(true),
    sidecars: Type.Array(ClientSideSidecar),
    // TODO(alon): Returning both per-channel limits and legacy max_sidecars for backward compatibility
    max_sidecars: Type.Number(),
    max_devices_per_channel: MaxDevicesPerChannelSchema,
    default_sidecar_frequency: Type.String(),
  }),
  ApiErrorResponse,
]);
export type ListSidecars = Static<typeof ListSidecarsSchema>;

export const DeleteSidecarSchema = Type.Union([Type.Object({ success: Type.Literal(true) }), ApiErrorResponse]);
export type DeleteSidecar = Static<typeof DeleteSidecarSchema>;

export const BulkReinviteSidecarsBody = Type.Object({
  sidecarIds: Type.Array(Type.String()),
});
export type BulkReinviteSidecarsBody = Static<typeof BulkReinviteSidecarsBody>;

export const BulkReinviteSidecarsSchema = Type.Union([Type.Object({ success: Type.Literal(true) }), ApiErrorResponse]);
export type BulkReinviteSidecars = Static<typeof BulkReinviteSidecarsSchema>;

export const channelTypeOptions: {
  label: string;
  value: Exclude<CreateSidecar["channelType"], undefined>;
  orgConfigKey: keyof Pick<
    OrgConfigs,
    "whatsapp_sidecars_enabled" | "telegram_sidecars_enabled" | "line_sidecars_enabled" | "signal_sidecars_enabled"
  >;
  maxDevicesConfigKey: keyof Pick<OrgConfigs, "max_devices_whatsapp" | "max_devices_telegram" | "max_devices_line" | "max_devices_signal">;
  linkWithPhoneDisabled?: boolean;
}[] = [
  {
    label: "WhatsApp",
    value: "whatsapp",
    orgConfigKey: "whatsapp_sidecars_enabled",
    maxDevicesConfigKey: "max_devices_whatsapp",
  },
  {
    label: "Telegram",
    value: "telegram",
    orgConfigKey: "telegram_sidecars_enabled",
    maxDevicesConfigKey: "max_devices_telegram",
  },
  {
    label: "Line",
    value: "line",
    orgConfigKey: "line_sidecars_enabled",
    maxDevicesConfigKey: "max_devices_line",
    linkWithPhoneDisabled: true,
  },
  {
    label: "Signal",
    value: "signal",
    orgConfigKey: "signal_sidecars_enabled",
    maxDevicesConfigKey: "max_devices_signal",
  },
];

export const channelTypeToFriendlyName = channelTypeOptions.reduce<Record<Exclude<CreateSidecar["channelType"], undefined>, string>>(
  (all, item) => {
    all[item.value] = item.label;
    return all;
  },
  { telegram: "", whatsapp: "", line: "", signal: "" }
);

export const channelTypeToGif: Record<keyof typeof channelTypeToFriendlyName, string> = {
  telegram: "",
  whatsapp: "https://assets.whatslly.com/email/imgs/pairing.gif",
  line: "",
  signal: "",
};

export const channelTypeToGifOnMobile: Record<keyof typeof channelTypeToFriendlyName, string> = {
  telegram: "",
  whatsapp: "https://assets.whatslly.com/email/imgs/pairing-v2.gif",
  line: "",
  signal: "",
};

export const getChannelTypeToGif = (channelType: Exclude<CreateSidecar["channelType"], undefined>, isMobile: boolean) => {
  return isMobile ? channelTypeToGifOnMobile[channelType] : channelTypeToGif[channelType];
};

export const channelTypeToPhoneVerifyType: Record<keyof typeof channelTypeToFriendlyName, "sendLoginCode" | "enterCode"> = {
  telegram: "sendLoginCode",
  whatsapp: "enterCode",
  line: "sendLoginCode",
  signal: "sendLoginCode",
};

export const channelShotNames: Record<keyof typeof channelTypeToFriendlyName, string> = {
  whatsapp: "wa",
  telegram: "tg",
  line: "ln",
  signal: "sg",
} as const;

export const channelTypeToK8S: Record<
  keyof typeof channelTypeToFriendlyName,
  {
    sidecarContainerName: string;
    sidecarCronContainerName: string;
    sidecarImage: string;
    k8sResources: { requests: { cpu: string; memory: string } };
    volumeMounts: { name: string; mountPath: string; readOnly?: boolean }[];
    volumes: {
      name: string;
      csi?: { driver: string; readOnly: boolean; volumeAttributes: Record<string, string> };
      persistentVolumeClaim?: { claimName: string };
    }[];
    env: { name: string; value?: string; valueFrom?: { secretKeyRef: { name: string; key: string } } }[] | undefined;
    mountEbsVolume: boolean;
    initContainers?: Array<{
      name: string;
      image: string;
      imagePullPolicy?: "Always" | "IfNotPresent" | "Never";
      args?: string[];
      command?: string[];
      env?: { name: string; value?: string; valueFrom?: { secretKeyRef: { name: string; key: string } } }[];
      volumeMounts?: { name: string; mountPath: string; readOnly?: boolean }[];
      ports?: { containerPort: number }[];
      dataVolumeMountPath?: string;
      restartPolicy?: "Always" | "Never";
    }>;
  }
> = {
  telegram: {
    mountEbsVolume: false,
    volumeMounts: [{ name: "secrets-store-inline", mountPath: "/mnt/secrets", readOnly: true }],
    volumes: [
      {
        name: "secrets-store-inline",
        csi: { driver: "secrets-store.csi.k8s.io", readOnly: true, volumeAttributes: { secretProviderClass: "aws-secrets-sidecars" } },
      },
    ],
    k8sResources: {
      requests: { cpu: "500m", memory: "500Mi" },
    },
    sidecarContainerName: "telegram-sidecar",
    sidecarCronContainerName: "telegram-sidecar-cron",
    sidecarImage: "542535883599.dkr.ecr.us-east-2.amazonaws.com/whatslly/telegram-sidecar",
    env: [
      {
        name: "TELEGRAM_API_ID",
        valueFrom: { secretKeyRef: { name: "telegram-secret", key: "telegram_api_id" } },
      },
      {
        name: "TELEGRAM_API_HASH",
        valueFrom: { secretKeyRef: { name: "telegram-secret", key: "telegram_api_hash" } },
      },
    ],
  },
  whatsapp: {
    mountEbsVolume: true,
    volumeMounts: [],
    volumes: [],
    k8sResources: { requests: { cpu: "500m", memory: "2000Mi" } },
    sidecarContainerName: "whatsapp-sidecar",
    sidecarCronContainerName: "whatsapp-sidecar-cron",
    sidecarImage: "542535883599.dkr.ecr.us-east-2.amazonaws.com/whatslly/whatsapp-sidecar",
    env: [
      {
        name: "NODE_OPTIONS",
        value: "--max-old-space-size=8196 --expose-gc",
      },
    ],
  },
  line: {
    mountEbsVolume: true,
    volumeMounts: [],
    volumes: [],
    k8sResources: {
      requests: { cpu: "500m", memory: "500Mi" },
    },
    sidecarContainerName: "line-sidecar",
    sidecarCronContainerName: "line-sidecar-cron",
    sidecarImage: "542535883599.dkr.ecr.us-east-2.amazonaws.com/whatslly/line-sidecar",
    env: undefined,
  },
  signal: {
    mountEbsVolume: true,
    volumeMounts: [],
    volumes: [],
    k8sResources: { requests: { cpu: "500m", memory: "500Mi" } },
    sidecarContainerName: "signal-sidecar",
    sidecarCronContainerName: "signal-sidecar-cron",
    sidecarImage: "542535883599.dkr.ecr.us-east-2.amazonaws.com/whatslly/signal-sidecar",
    initContainers: [
      {
        name: "signal-rest",
        image: "542535883599.dkr.ecr.us-east-2.amazonaws.com/whatslly/signal-cli-rest-api:latest",
        imagePullPolicy: "IfNotPresent",
        env: [{ name: "MODE", value: "native" }],
        ports: [{ containerPort: 8080 }],
        dataVolumeMountPath: "/home/.local/share/signal-cli",
        restartPolicy: "Always",
      },
    ],
    env: undefined,
  },
};

export const channelI18n: Record<
  keyof typeof channelTypeToFriendlyName,
  { en: Record<string, string>; es: Record<string, string>; pt: Record<string, string> }
> = {
  telegram: {
    en: flattenResources({
      instructions: {
        how_to_connect: "How to connect:",
        instruction_1: 'Click the "Pair with Tuvis" button above.',
        instruction_2: "You'll be taken to a page with a QR code.",
        instruction_3: "Open Telegram on your phone.",
        instruction_4: "Go to Settings > Devices > Link Desktop Device.",
        instruction_5: "Use your phone to scan the QR code on your computer screen.",
        instruction_6: "Wait for the connection to be established.",
      },
    }),
    es: flattenResources({
      instructions: {
        how_to_connect: "Cómo conectar:",
        instruction_1: 'Haz clic en el botón "Conectar con Tuvis" arriba',
        instruction_2: "Serás llevado a una página con un código QR",
        instruction_3: "Abre Telegram en tu teléfono.",
        instruction_4: "Ve a Ajustes > Dispositivos  > Vincular un dispositivo.",
        instruction_5: "Usa tu teléfono para escanear el código QR en la pantalla de tu computadora.",
        instruction_6: "Espera a que se establezca la conexión.",
      },
    }),
    pt: flattenResources({
      instructions: {
        how_to_connect: "Como conectar:",
        instruction_1: 'Clique no botão acima "Conecte-se com a Tuvis',
        instruction_2: "Você será levado a uma página com um código QR",
        instruction_3: "Abra o Telegram no seu celular",
        instruction_4: "Vá para Configurações > Dispositivos > Conectar desktop",
        instruction_5: "Use seu telefone para escanear o código QR na tela do seu computador",
        instruction_6: "Aguarde a conexão ser estabelecida",
      },
    }),
  },
  whatsapp: {
    en: flattenResources({
      instructions: {
        how_to_connect: "How to connect:",
        instruction_1: 'Click the "Pair with Tuvis" button above.',
        instruction_2: "You'll be taken to a page with a QR code.",
        instruction_3: "Open WhatsApp on your phone.",
        instruction_4: "Go to Settings > Linked Devices > Link a Device.",
        instruction_5: "Use your phone to scan the QR code on your computer screen.",
        instruction_6: "Wait for the connection to be established.",
      },
    }),
    es: flattenResources({
      instructions: {
        how_to_connect: "Cómo conectar:",
        instruction_1: 'Haz clic en el botón "Conectar con Tuvis" arriba.',
        instruction_2: "Serás llevado a una página con un código QR.",
        instruction_3: "Abre WhatsApp en tu teléfono.",
        instruction_4: "Ve a Configuración > Dispositivos vinculados > Vincular un dispositivo.",
        instruction_5: "Usa tu teléfono para escanear el código QR en la pantalla de tu computadora.",
        instruction_6: "Espera a que se establezca la conexión.",
      },
    }),
    pt: flattenResources({
      instructions: {
        how_to_connect: "Como conectar:",
        instruction_1: 'Clique no botão acima "Conecte-se com a Tuvis".',
        instruction_2: "Você será levado a uma página com um código QR.",
        instruction_3: "Abra o WhatsApp no seu telefone.",
        instruction_4: "Vá para Configurações > Aparelhos conectados > Conectar um aparelho.",
        instruction_5: "Use seu telefone para escanear o código QR na tela do seu computador.",
        instruction_6: "Aguarde a conexão ser estabelecida.",
      },
    }),
  },
  line: {
    en: flattenResources({
      instructions: {
        how_to_connect: "How to connect:",
        instruction_1: 'Click the "Pair with Tuvis" button above.',
        instruction_2: "You'll be taken to a page with a QR code.",
        instruction_3: "Open WhatsApp on your phone.",
        instruction_4: "Go to Settings > Linked Devices > Link a Device.",
        instruction_5: "Use your phone to scan the QR code on your computer screen.",
        instruction_6: "Wait for the connection to be established.",
      },
    }),
    es: flattenResources({
      instructions: {
        how_to_connect: "Cómo conectar:",
        instruction_1: 'Haz clic en el botón "Conectar con Tuvis" arriba.',
        instruction_2: "Serás llevado a una página con un código QR.",
        instruction_3: "Abre WhatsApp en tu teléfono.",
        instruction_4: "Ve a Configuración > Dispositivos vinculados > Vincular un dispositivo.",
        instruction_5: "Usa tu teléfono para escanear el código QR en la pantalla de tu computadora.",
        instruction_6: "Espera a que se establezca la conexión.",
      },
    }),
    pt: flattenResources({
      instructions: {
        how_to_connect: "Como conectar:",
        instruction_1: 'Clique no botão acima "Conecte-se com a Tuvis".',
        instruction_2: "Você será levado a uma página com um código QR.",
        instruction_3: "Abra o WhatsApp no seu telefone.",
        instruction_4: "Vá para Configurações > Aparelhos conectados > Conectar um aparelho.",
        instruction_5: "Use seu telefone para escanear o código QR na tela do seu computador.",
        instruction_6: "Aguarde a conexão ser estabelecida.",
      },
    }),
  },
  signal: {
    en: flattenResources({
      instructions: {
        how_to_connect: "How to connect:",
        instruction_1: 'Click the "Pair with Tuvis" button above.',
        instruction_2: "You'll be taken to a page with a QR code.",
        instruction_3: "Open Signal on your phone.",
        instruction_4: "Go to Settings > Linked Devices > Link a Device.",
        instruction_5: "Use your phone to scan the QR code on your computer screen.",
        instruction_6: "Wait for the connection to be established.",
      },
    }),
    es: flattenResources({
      instructions: {
        how_to_connect: "Cómo conectar:",
        instruction_1: 'Haz clic en el botón "Conectar con Tuvis" arriba.',
        instruction_2: "Serás llevado a una página con un código QR.",
        instruction_3: "Abre Signal en tu teléfono.",
        instruction_4: "Ve a Configuración > Dispositivos vinculados > Vincular un dispositivo.",
        instruction_5: "Usa tu teléfono para escanear el código QR en la pantalla de tu computadora.",
        instruction_6: "Espera a que se establezca la conexión.",
      },
    }),
    pt: flattenResources({
      instructions: {
        how_to_connect: "Como conectar:",
        instruction_1: 'Clique no botão acima "Conecte-se com a Tuvis".',
        instruction_2: "Você será levado a uma página com um código QR.",
        instruction_3: "Abra o Signal no seu telefone.",
        instruction_4: "Vá para Configurações > Aparelhos conectados > Conectar um aparelho.",
        instruction_5: "Use seu telefone para escanear o código QR na tela do seu computador.",
        instruction_6: "Aguarde a conexão ser estabelecida.",
      },
    }),
  },
};

export const GetSidecarCountQueryParamsSchema = Type.Object({
  orgId: Type.String(),
});
export type GetSidecarCountQueryParams = Static<typeof GetSidecarCountQueryParamsSchema>;

export const GetSidecarCountSchema = Type.Union([Type.Object({ success: Type.Literal(true), count: Type.Number() }), ApiErrorResponse]);
export type GetSidecarCount = Static<typeof GetSidecarCountSchema>;

export const GetSidecarOnboardingStatsQueryParamsSchema = Type.Object({
  orgId: Type.String(),
  userId: Type.Optional(Type.Union([Type.String(), Type.Array(Type.String())])),
});
export type GetSidecarOnboardingStatsQueryParams = Static<typeof GetSidecarOnboardingStatsQueryParamsSchema>;

export const SidecarOnboardingStatsDataSchema = Type.Object({
  connectedCount: Type.Number(),
  totalRegisteredDevices: Type.Number(),
  connectedRatePercent: Type.Number(),
});
export type SidecarOnboardingStatsData = Static<typeof SidecarOnboardingStatsDataSchema>;

export const GetSidecarOnboardingStatsSchema = Type.Union([
  Type.Object({ success: Type.Literal(true), data: SidecarOnboardingStatsDataSchema }),
  ApiErrorResponse,
]);
export type GetSidecarOnboardingStats = Static<typeof GetSidecarOnboardingStatsSchema>;

export const SidecarSnapshotInfo = Type.Object({
  name: Type.String(),
  namespace: Type.String(),
  pvcName: Type.String(),
  sidecarId: Type.String(),
  orgId: Type.String(),
  readyToUse: Type.Boolean(),
  restoreSize: Type.String(),
  createdAt: Type.String({ format: "date-time" }),
  expiresAt: Type.String({ format: "date-time" }),
});
export type SidecarSnapshotInfo = Static<typeof SidecarSnapshotInfo>;

export const ListSidecarSnapshotsSchema = Type.Union([
  Type.Object({ success: Type.Literal(true), snapshots: Type.Array(SidecarSnapshotInfo), notReadyCount: Type.Number() }),
  ApiErrorResponse,
]);
export type ListSidecarSnapshots = Static<typeof ListSidecarSnapshotsSchema>;

export const ApplySnapshotRequestSchema = Type.Object({
  snapshotName: Type.Optional(Type.String()),
});
export type ApplySnapshotRequest = Static<typeof ApplySnapshotRequestSchema>;

export const ApplySnapshotResponseSchema = Type.Union([Type.Object({ success: Type.Literal(true) }), ApiErrorResponse]);
export type ApplySnapshotResponse = Static<typeof ApplySnapshotResponseSchema>;
